import { getConnectivityOnline } from "@/domains/offline/connectivity/connectivityStore";
import { isDeployEnforcementHandling } from "@/domains/shared/services/deployVersion";
import {
  forceLogoutSuperseded,
  SESSION_SUPERSEDED_DEFAULT_MESSAGE,
} from "@/domains/shared/services/sessionSuperseded";
import { isSessionSupersededDetail } from "@/domains/shared/services/sessionSupersededDetail";
import {
  isTherapistBlockedShellPath,
  resolveClinicalAccessRedirectPath,
  SECRETARY_HOME_PATH,
  SECRETARY_PROFILE_PATH,
} from "@/domains/subscription/accountAccess";
import { CLINIC_HOME_PATH, CLINIC_SUBSCRIPTION_PATH } from "@/domains/multi/clinicMemberPaths";
import { isPaymentRequiredResponse } from "@/domains/subscription/paymentRequired";
import { isTrialExpiredResponse } from "@/domains/subscription/trialExpired";
import { ApiMessageCodes } from "@/domains/shared/services/apiMessageCodes";
import type { AxiosInstance } from "axios";

type ErrorBody = { detail?: unknown; message?: string; reason?: unknown; code?: unknown };

function isClinicalAccessForbiddenResponse(data: unknown): boolean {
  if (isTrialExpiredResponse(data) || isPaymentRequiredResponse(data)) {
    return true;
  }

  if (!data || typeof data !== "object") {
    return false;
  }

  const payload = data as { code?: unknown; reason?: unknown };
  return payload.code === ApiMessageCodes.billingOnly
    || payload.reason === ApiMessageCodes.billingOnly
    || payload.code === ApiMessageCodes.orgBillingBlocked
    || payload.reason === ApiMessageCodes.orgBillingBlocked;
}

function isAllowedClinicalBlockPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "");
  return isTherapistBlockedShellPath(pathname)
    || pathname === "/verificar-email"
    || normalized === CLINIC_SUBSCRIPTION_PATH
    || normalized === CLINIC_HOME_PATH
    || normalized.startsWith(CLINIC_HOME_PATH + "/")
    || normalized === "/profissional"
    || normalized === SECRETARY_HOME_PATH
    || normalized === SECRETARY_PROFILE_PATH;
}

export function setupSessionInterceptor(api: AxiosInstance): void {
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error?.__mivoDeployHandled === true || isDeployEnforcementHandling()) {
        return Promise.reject(error);
      }

      if (!getConnectivityOnline()) {
        return Promise.reject(error);
      }

      const status = error?.response?.status;
      const data = error?.response?.data as ErrorBody | undefined;

      if (status === 401 && isSessionSupersededDetail(data?.detail)) {
        const message =
          typeof data?.message === "string" && data.message.trim().length > 0
            ? data.message
            : SESSION_SUPERSEDED_DEFAULT_MESSAGE;
        await forceLogoutSuperseded(message);
      }

      if (status === 403 && isClinicalAccessForbiddenResponse(data)) {
        if (!isAllowedClinicalBlockPath(window.location.pathname)) {
          const reason = typeof data?.reason === "string"
            ? data.reason
            : typeof data?.code === "string"
              ? data.code
              : null;
          const onSecretaryShell = window.location.pathname.startsWith("/administrativo");
          const redirectPath = onSecretaryShell
            ? SECRETARY_HOME_PATH
            : resolveClinicalAccessRedirectPath({
                clinicalAccessBlocked: true,
                clinicalAccessBlockedReason: reason,
              });
          window.location.assign(redirectPath);
        }
      }

      return Promise.reject(error);
    },
  );
}
